import { useEffect, useState, useTransition } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label, Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { useDict, useLocale, fill } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";
import type { User } from "@/lib/types";

type PublicJobDetail = {
  id: string;
  title: string;
  company_name: string;
  location: string | null;
  expires_at: string | null;
  generated_jd: string | null;
  language: string;
  created_at: string;
};

const MAX_CV_BYTES = 5 * 1024 * 1024;

export function CareerDetailPage({ user }: { user: User | null | undefined }) {
  const { id } = useParams<{ id: string }>();
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const navigate = useNavigate();
  const [job, setJob] = useState<PublicJobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cv, setCv] = useState<File | null>(null);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    if (!id) return;
    let active = true;
    fetch(`/api/careers/${id}`)
      .then(async (res) => {
        if (res.status === 404) {
          if (active) setNotFound(true);
          return null;
        }
        if (!res.ok) throw new Error("Failed");
        return res.json() as Promise<{ job: PublicJobDetail }>;
      })
      .then((data) => {
        if (active && data) setJob(data.job);
      })
      .catch(() => {
        if (active) setError(t.errors.networkError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, t.errors.networkError]);

  function apply() {
    if (!id) return;
    if (cv && cv.size > MAX_CV_BYTES) {
      setError(t.errors.cvTooLarge);
      return;
    }
    setError(null);
    startTransition(async () => {
      try {
        const fd = new FormData();
        if (cv) fd.append("file", cv);
        const res = await fetch(`/api/careers/${id}/apply`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        const data = (await res.json()) as {
          error?: string;
          code?: string;
          redirect?: string;
        };
        if (res.status === 401) {
          navigate(
            `/sign-in?redirect=${encodeURIComponent(`/careers/${id}`)}`,
          );
          return;
        }
        if (res.status === 403) {
          setError(t.careers.signInToApply);
          return;
        }
        if (!res.ok || data.error) {
          setError(resolveApiError(t, data, "applyFailed"));
          return;
        }
        if (data.redirect) navigate(data.redirect);
      } catch {
        setError(t.errors.networkError);
      }
    });
  }

  if (loading || user === undefined) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Not found
      </div>
    );
  }
  if (error && !job) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error}
      </div>
    );
  }
  if (!job) return null;

  const isCandidate = user?.role === "candidate";
  const redirectPath = `/careers/${job.id}`;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/careers"
        className="inline-flex items-center gap-1 text-sm text-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t.careers.backToCareers}
      </Link>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <h1 className="text-2xl font-bold">{job.title}</h1>
            <p className="mt-1 text-muted">
              {job.company_name}
              {" · "}
              {job.location || t.careers.remote}
            </p>
            {job.expires_at && (
              <p className="mt-1 text-sm text-muted">
                {fill(t.careers.expires, {
                  date: format(new Date(job.expires_at), "PP", {
                    locale: dateLocale,
                  }),
                })}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <Markdown>
              {job.generated_jd ?? `_${t.jobs.noDescription}_`}
            </Markdown>
          </div>

          {isCandidate ? (
            <div className="space-y-3 border-t border-border pt-4">
              <div>
                <Label htmlFor="cv">{t.careers.cvOptional}</Label>
                <Input
                  id="cv"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setCv(e.target.files?.[0] || null)}
                />
              </div>
              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              )}
              <Button onClick={apply} loading={pending}>
                {t.careers.apply}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              <Link
                to={`/sign-in?redirect=${encodeURIComponent(redirectPath)}`}
              >
                <Button>{t.careers.signInToApply}</Button>
              </Link>
              <Link
                to={`/sign-up?as=candidate&redirect=${encodeURIComponent(redirectPath)}`}
              >
                <Button variant="outline">{t.careers.signUpToApply}</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
